import { Helmet } from "react-helmet-async";

const SITE_URL = "https://energize.fit";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESC =
    "Personalized wellness guidance that adapts to your nutrition, movement, sleep, stress and daily routines. Built for real life.";

/**
 * SEO — per-page meta tags + canonical + OG/Twitter + optional JSON-LD.
 *
 * Usage:
 *   <SEO
 *     title="Pricing — Energize.fit"
 *     description="Personalised wellness plans..."
 *     path="/pricing"
 *     image="https://.../og.jpg" (optional)
 *     type="website" | "article" (optional)
 *     jsonLd={{...}} (optional, object or array of objects)
 *     noindex={true} (optional)
 *   />
 */
export default function SEO({
    title,
    description = DEFAULT_DESC,
    path = "/",
    image = DEFAULT_OG,
    type = "website",
    jsonLd = null,
    noindex = false,
    keywords = null,
    publishedTime = null,
    modifiedTime = null,
    author = null,
}) {
    const canonical = `${SITE_URL}${path}`;
    const fullTitle = title || "The Adaptive Wellness Platform | Energize.fit";
    const ldBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={canonical} />
            {noindex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Energize.fit" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:locale" content="en_IN" />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            {author && <meta property="article:author" content={author} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@BeEnergizedFit" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* JSON-LD structured data */}
            {ldBlocks.map((block, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(block)}
                </script>
            ))}
        </Helmet>
    );
}

/** Site-wide Organisation + WebSite structured data — mount once on Home. */
export const ORG_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Energize.fit",
    url: SITE_URL,
    logo: "https://customer-assets.emergentagent.com/job_vibrant-wellness-5/artifacts/ibtyzilr_Energize.fit%20logo%20Final%20Opt.png",
    sameAs: [
        "https://www.instagram.com/beenergizedfit",
        "https://www.linkedin.com/company/energize-fit",
    ],
    contactPoint: [
        {
            "@type": "ContactPoint",
            email: "hello@energize.fit",
            contactType: "customer support",
            areaServed: "IN",
            availableLanguage: ["English", "Hindi"],
        },
    ],
};

export const WEBSITE_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Energize.fit",
    url: SITE_URL,
    potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/blog?q={search_term_string}`,
        "query-input": "required name=search_term_string",
    },
};

export { SITE_URL, DEFAULT_OG };
